import { useEffect } from 'react';

export type ArrowKeyNavigation = {
  /** Current selected index */
  selectedIdx: number;
  /** Setter for selected index */
  setSelectedIdx: (idx: number) => void;
  /** Maximum index (inclusive) */
  maxIdx: number;
  /** Minimum index (default 0) */
  minIdx?: number;
};

/**
 * Left/right arrow stepping over a bounded index, wrapping at both ends.
 * Anything that is not an arrow key leaves the index alone.
 *
 * Exported beside the hook because a listener on `window` is not always where
 * the keys arrive: inside a modal overlay, the dialog primitive stops arrow
 * keys at its popup so composite widgets within it cannot leak keys to the page
 * — so an overlay attaches this to the popup's own `onKeyDown` instead. One
 * definition of the stepping either way.
 */
export function handleArrowKeyNavigation(
  key: string,
  { selectedIdx, setSelectedIdx, maxIdx, minIdx = 0 }: ArrowKeyNavigation
): void {
  if (key === 'ArrowLeft') {
    setSelectedIdx(selectedIdx <= minIdx ? maxIdx : selectedIdx - 1);
  } else if (key === 'ArrowRight') {
    setSelectedIdx(selectedIdx >= maxIdx ? minIdx : selectedIdx + 1);
  }
}

/**
 * useArrowKeyNavigation
 * Handles left/right arrow key navigation for a given index, with min/max bounds and an optional enabled flag.
 * @param enabled - Whether the navigation is active
 * @param selectedIdx - Current selected index
 * @param setSelectedIdx - Setter for selected index
 * @param maxIdx - Maximum index (inclusive)
 * @param minIdx - Minimum index (default 0)
 */
export function useArrowKeyNavigation({
  enabled,
  selectedIdx,
  setSelectedIdx,
  maxIdx,
  minIdx = 0,
}: ArrowKeyNavigation & { enabled: boolean }) {
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      handleArrowKeyNavigation(e.key, { selectedIdx, setSelectedIdx, maxIdx, minIdx });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, selectedIdx, setSelectedIdx, maxIdx, minIdx]);
}
